/* diwali26 early access.
 *
 * The sale is built and published, and rendered hidden. This is what reveals it for
 * someone holding the password.
 *
 * The password is never in the theme. Each product's claim code is stored encrypted in a
 * metafield, and the only way to turn ciphertext back into a code is to type the right
 * word. That is also how the password is checked: there is no hash to compare against,
 * the browser simply tries to decrypt, and AES-GCM's auth tag fails on a wrong key. So
 * nothing in page source helps anybody, which was the whole problem with putting the
 * codes in a data attribute and hiding the element with css.
 */
(function () {
  "use strict";

  var KEY = "dw26:pass";
  var SALT = "wearadhd-dw26-v1";
  var ITERATIONS = 100000;

  function readStore() { try { return window.localStorage.getItem(KEY); } catch (e) { return null; } }
  function writeStore(v) { try { window.localStorage.setItem(KEY, v); } catch (e) {} }
  function dropStore() { try { window.localStorage.removeItem(KEY); } catch (e) {} }

  function bytes(str) { return new TextEncoder().encode(str); }

  function fromBase64(b64) {
    var bin = window.atob(b64);
    var out = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }

  // Upper-cased, because this gets typed on a phone keyboard that capitalises the first
  // letter whether or not anyone asked it to.
  function deriveKey(password) {
    var subtle = window.crypto && window.crypto.subtle;
    if (!subtle) return Promise.reject(new Error("no subtle crypto"));
    return subtle
      .importKey("raw", bytes(String(password).trim().toUpperCase()), "PBKDF2", false, ["deriveKey"])
      .then(function (material) {
        return subtle.deriveKey(
          { name: "PBKDF2", salt: bytes(SALT), iterations: ITERATIONS, hash: "SHA-256" },
          material,
          { name: "AES-GCM", length: 256 },
          false,
          ["decrypt"],
        );
      });
  }

  function decrypt(key, b64) {
    var raw = fromBase64(b64);
    return window.crypto.subtle
      .decrypt({ name: "AES-GCM", iv: raw.subarray(0, 12) }, key, raw.subarray(12))
      .then(function (buf) { return new TextDecoder().decode(buf); });
  }

  /** Everything liquid rendered hidden because the sale is not open to everyone yet. */
  function reveal() {
    document
      .querySelectorAll('[data-dw26-card-preview="1"], [data-dw26-clock-preview="1"], [data-dw26-badge-preview="1"]')
      .forEach(function (el) {
        el.removeAttribute("hidden");
        // The card swap replaces the theme's own price, which is its sibling.
        var list = el.parentElement && el.parentElement.querySelector("price-list");
        if (list) list.setAttribute("hidden", "");
      });

    var bar = document.querySelector('[data-dw26][data-dw26-preview="1"]');
    if (bar) {
      bar.removeAttribute("hidden");
      bar.dataset.dw26Preview = "shown";
    }

    document.dispatchEvent(new Event("dw26:refresh"));
  }

  /**
   * Try a password against whatever ciphertext this page carries.
   *
   * A pdp has the claim bar's own code; a collection page has none, so there is nothing
   * to decrypt and the password is accepted on the strength of having been stored. That
   * is deliberate: the password only gates what a person sees, and the thing it actually
   * protects, the code itself, is only ever on a page that carries it.
   */
  function apply(password) {
    return deriveKey(password).then(function (key) {
      var bar = document.querySelector("[data-dw26][data-code-enc]");
      if (!bar) {
        unlocked();
        return true;
      }
      return decrypt(key, bar.getAttribute("data-code-enc")).then(
        function (code) {
          bar.dataset.code = code;
          unlocked();
          return true;
        },
        function () { return false; },
      );
    });
  }

  function unlocked() {
    window.__dw26Unlocked = true;
    reveal();
    document.querySelectorAll("[data-dw26-unlock]").forEach(function (f) {
      f.setAttribute("hidden", "");
    });
  }

  /**
   * Whether the sale is still behind the password.
   *
   * Liquid stops emitting these preview markers the moment dw26_enabled is switched on,
   * so their absence is the same signal boot() already gates the prompt on. Asking the
   * dom rather than passing the setting through keeps one source of truth.
   */
  function stillGated() {
    return !!document.querySelector(
      '[data-dw26-card-preview="1"], [data-dw26-clock-preview="1"], [data-dw26-badge-preview="1"], [data-dw26][data-code-enc]',
    );
  }

  /**
   * The sale collection, split in two.
   *
   * Above the line are the twelve capped claims, which is what early access is early
   * to. Below is the rest of the markdown, which is not claimable and not discounted
   * until midnight, so it needs saying rather than leaving people to work out why most
   * of the page has no yellow on it.
   *
   * The split is found by looking for the last card carrying a claim badge rather than
   * counting to twelve, because the badge renders whether or not it is visible and the
   * collection order is yours to change without touching this.
   */
  function splitCollection() {
    if (!/^\/collections\/diwali-sale/.test(window.location.pathname)) return;

    var grid = document.querySelector("product-list, .product-list");
    if (!grid) return;

    // Once the sale is open to everyone the split still means something, but the early
    // access framing and the password box do not: nobody arriving now has a word, and
    // asking for one reads as a shop that is not open yet.
    var gated = stillGated();

    // The grid hydrates progressively, so the first pass can land while only a couple
    // of cards exist and put the rule after the second product. Re-run whenever the
    // grid changes and move it, rather than trusting one reading of a half-built page.
    if (!grid.dataset.dw26Watch && "MutationObserver" in window) {
      grid.dataset.dw26Watch = "1";
      var timer = null;
      new MutationObserver(function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(splitCollection, 120);
      }).observe(grid, { childList: true });
    }

    var cards = [].slice.call(grid.children).filter(function (el) {
      return !el.hasAttribute("data-dw26-split");
    });
    var lastClaim = -1;
    cards.forEach(function (card, i) {
      if (card.querySelector(".badge--dw26-claim")) lastClaim = i;
    });
    if (lastClaim < 0 || lastClaim >= cards.length - 1) return;

    // Already in the right place, nothing to do.
    var existing = grid.querySelector("[data-dw26-split]");
    if (existing && existing.previousElementSibling === cards[lastClaim]) return;
    if (existing) existing.remove();

    var rule = document.createElement("div");
    rule.className = "dw26-split";
    rule.setAttribute("data-dw26-split", "");
    rule.innerHTML =
      '<span class="dw26-split__line"></span>' +
      '<span class="dw26-split__text">' +
      (gated ? 'the rest of the sale, live at midnight' : 'the rest of the sale') +
      '</span>' +
      '<span class="dw26-split__line"></span>';
    cards[lastClaim].after(rule);

    // The band goes in once; only the rule moves as cards arrive.
    if (grid.parentElement.querySelector("[data-dw26-band]")) return;

    var band = document.createElement("div");
    band.className = "dw26-band";
    band.setAttribute("data-dw26-band", "");
    band.innerHTML =
      '<p class="dw26-band__kicker">' + (gated ? 'early access' : 'all day diwali sale') + '</p>' +
      '<p class="dw26-band__head">' + (lastClaim + 1) + ' capped drops, claimable now</p>' +
      '<p class="dw26-band__sub">everything below the line is part of the sale too' +
      (gated ? ', and goes live at midnight.' : '.') +
      '</p>';

    if (gated) {
      var form = document.createElement("form");
      form.className = "dw26-band__form";
      form.setAttribute("data-dw26-unlock", "");
      form.setAttribute("autocomplete", "off");
      form.innerHTML =
        '<input class="dw26-band__input" data-dw26-unlock-input type="text" ' +
        'inputmode="text" autocapitalize="characters" autocorrect="off" spellcheck="false" ' +
        'placeholder="your word" aria-label="early access word">' +
        '<button class="dw26-band__go" data-dw26-unlock-submit type="submit">let me in</button>' +
        '<p class="dw26-band__note" data-dw26-unlock-note aria-live="polite"></p>';
      band.appendChild(form);
      wire(form);
      // Already unlocked by a stored word before this ran: hide it straight away.
      if (window.__dw26Unlocked) form.setAttribute("hidden", "");
    }

    grid.parentElement.insertBefore(band, grid);

    // One prompt per page. The fixed bar is the fallback for a pdp reached directly;
    // on the collection the band is the thing people should see.
    var fixed = document.querySelector(".dw26-unlock");
    if (fixed) fixed.setAttribute("hidden", "");
  }

  function boot() {
    // Drawn before anything else and regardless of lock state: the split and its
    // heading are how the page reads, not part of the gate. ?early=off still wants it.
    splitCollection();

    // ?early=off, so the shape of the locked site can be checked without clearing
    // site data by hand.
    var qs = new URLSearchParams(window.location.search);
    if (qs.get("early") === "off") {
      dropStore();
      return;
    }

    var stored = readStore();
    if (stored) {
      apply(stored).then(function (ok) {
        // A stored password that no longer works means it was changed; drop it rather
        // than leaving someone stuck looking at a locked site with no prompt.
        if (!ok) dropStore();
      });
    }

    var form = document.querySelector(".dw26-unlock[data-dw26-unlock]");
    if (!form) return;

    // Only ask on a page that has something to unlock. Liquid renders this on every
    // page because it lives in the layout, but on a contact page or a product that is
    // not in the sale there is nothing behind it, and a password prompt with nothing
    // behind it just makes the shop look broken to the people who do not have the word.
    var gated = document.querySelector(
      '[data-dw26-card-preview="1"], [data-dw26-clock-preview="1"], [data-dw26-badge-preview="1"], [data-dw26][data-code-enc]',
    );
    if (!gated) {
      form.setAttribute("hidden", "");
      return;
    }

    wire(form);
  }

  /** Same submit behaviour wherever the prompt is drawn. */
  function wire(form) {
    var input = form.querySelector("[data-dw26-unlock-input]");
    var note = form.querySelector("[data-dw26-unlock-note]");
    var button = form.querySelector("[data-dw26-unlock-submit]");

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var value = input.value.trim();
      if (!value) return;

      if (note) note.textContent = "checking";
      if (button) button.disabled = true;

      apply(value).then(
        function (ok) {
          if (button) button.disabled = false;
          if (ok) {
            writeStore(value);
            if (note) note.textContent = "";
          } else {
            if (note) note.textContent = "that is not the word";
            input.select();
          }
        },
        function () {
          if (button) button.disabled = false;
          if (note) note.textContent = "something went wrong, try again";
        },
      );
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
